import { apiurl } from "../../common/constants";
import Alert from "../Alert";
import { useState, useRef } from "react";
import { BrandSelect, CategoryWithSubCategorySelect } from "../subcomponents/Selectors";
import UploadProgressBar from "../subcomponents/UploadProgressBar";
import { useStore } from "../../context/StoreContext";
import { useAuthAxios } from "../authfetch";

const AddProduct = () => {
  const { brands, categories } = useStore();
  const authAxios = useAuthAxios();
  
  // Form state
  const [formData, setFormData] = useState({
    prod_name: "",
    prod_desc: "",
    prod_price: "",
    prod_discounted_price: 0,
    prod_stock: "",
    brand_ref: "",
    category_ref: "",
    pre_order: false,
  });
  
  const [images, setImages] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle brand selection
  const handleBrandChange = (value) => {
    setFormData(prev => ({
      ...prev,
      brand_ref: value
    }));
  };

  // Handle category selection
  const handleCategoryChange = (value) => {
    setFormData(prev => ({
      ...prev,
      category_ref: value
    }));
  };

  const handlePreOrderChange = (value) => {
    setFormData(prev => ({
      ...prev,
      pre_order: value
    }));
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) {
      setImages([]);
      setThumbnailIndex(null);
      return;
    }

    const imagePromises = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            file,
            name: file.name,
            preview: event.target.result
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then((loadedImages) => {
      setImages(loadedImages);
      setThumbnailIndex(0); // Auto-select first image as thumbnail
    });
  };

  // Handle thumbnail selection
  const handleThumbnailSelect = (index) => {
    setThumbnailIndex(index);
  };

  // Validate form
  const validateForm = () => {
    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return false;
    }

    if (!formData.brand_ref || formData.brand_ref === "Select Brand") {
      setAlertMessage({ text: "Please select a brand to continue", type: "warning" });
      return false;
    }

    if (!formData.category_ref || formData.category_ref === "Select Category/SubCategory") {
      setAlertMessage({ text: "Please select a Category/SubCategory to continue", type: "warning" });
      return false;
    }

    if (images.length === 0) {
      setAlertMessage({ text: "Please upload at least one product image", type: "warning" });
      return false;
    }

    if (thumbnailIndex === null) {
      setAlertMessage({ text: "Please select a thumbnail image", type: "warning" });
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = new FormData();
    submitData.append("prod_name", formData.prod_name);
    submitData.append("prod_desc", formData.prod_desc);
    submitData.append("prod_price", formData.prod_price);
    submitData.append("prod_discounted_price", formData.prod_discounted_price);
    submitData.append("prod_stock", formData.prod_stock);
    submitData.append("brand_ref", formData.brand_ref);
    submitData.append("category_ref", formData.category_ref);
    submitData.append("product_thumbnail", images[thumbnailIndex].name);
    submitData.append("pre_order", formData.pre_order);

    images.forEach((img) => {
      submitData.append("prod_img", img.file);
    });

    setShowProgress(true);
    setAlertMessage(null);

    try {
      const { data } = await authAxios.post(`${apiurl}/products`, submitData, {
        headers: {
          'Content-Type': undefined
        },
        onUploadProgress: (event) => {
          const percent = (event.loaded / event.total);
          setUploadProgress(percent);
        }
      });

      setAlertMessage({ text: data.message, type: "success" });
      resetForm();
    } catch (err) {
      console.error("Error occurred:", err);
      setAlertMessage({ 
        text: err.response?.data?.message || "An error occurred while adding the product", 
        type: "error" 
      });
    } finally {
      setShowProgress(false);
      setUploadProgress(0);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      prod_name: "",
      prod_desc: "",
      prod_price: "",
      prod_discounted_price: 0,
      prod_stock: "",
      brand_ref: "",
      category_ref: "",
      pre_order: false,
    });
    setImages([]);
    setThumbnailIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <form className="addProductForm" ref={formRef} onSubmit={handleSubmit}>
      {alertMessage && (
        <div className="alertbox mb-3">
          <Alert message={alertMessage.text} type={alertMessage.type} />
        </div>
      )}

      <div className="input-group mb-3">
        <span className="input-group-text">Name</span>
        <input
          required
          type="text"
          name="prod_name"
          className="form-control"
          placeholder="Product Name"
          value={formData.prod_name}
          onChange={handleInputChange}
        />
      </div>

      <div className="input-group mb-3">
        <span className="input-group-text">Desc</span>
        <textarea
          required
          name="prod_desc"
          className="form-control"
          placeholder="Product Description"
          style={{ height: "150px" }}
          value={formData.prod_desc}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="preOrderSwitch"
          name="pre_order"
          checked={formData.pre_order}
          onChange={(e) => handlePreOrderChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="preOrderSwitch">
          Pre Order
        </label>
      </div>

      <div className="input-group mb-3">
        <span className="input-group-text">Price</span>
        <input
          required
          type="number"
          name="prod_price"
          className="form-control"
          placeholder="1999"
          value={formData.prod_price}
          onChange={handleInputChange}
        />
      </div>

      <div className="input-group mb-3">
        <span className="input-group-text">Discounted Price</span>
        <input
          required
          type="number"
          name="prod_discounted_price"
          className="form-control"
          placeholder="999"
          value={formData.prod_discounted_price}
          onChange={handleInputChange}
        />
      </div>

      <div className="input-group mb-3">
        <span className="input-group-text">Stock</span>
        <input
          required
          type="number"
          name="prod_stock"
          className="form-control"
          placeholder="10"
          value={formData.prod_stock}
          onChange={handleInputChange}
        />
      </div>

      <div className="row mt-3">
        <div className="col">
          <BrandSelect 
            brands={brands} 
            brandId="addBrandSelect" 
            onChange={handleBrandChange}
            value={formData.brand_ref}
          />
        </div>
        <div className="col">
          <CategoryWithSubCategorySelect
            categories={categories}
            selectId="addCategorySelect"
            isRequired={true}
            firstOption="Select Category/SubCategory"
            disableFirstOption={true}
            onChange={handleCategoryChange}
            value={formData.category_ref}
          />
        </div>
      </div>

      <div className="my-3">
        <input
          ref={fileInputRef}
          className="form-control"
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageChange}
          required
        />
        
        {images.length > 0 && (
          <div className="mt-3">
            <p className="fw-bold">Select Thumbnail:</p>
            <div className="d-flex flex-wrap gap-2">
              {images.map((img, index) => (
                <div 
                  key={index} 
                  className="position-relative"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleThumbnailSelect(index)}
                >
                  <input
                    type="radio"
                    name="product_thumbnail"
                    checked={thumbnailIndex === index}
                    onChange={() => handleThumbnailSelect(index)}
                    className="position-absolute top-0 start-0 m-2"
                    style={{ zIndex: 1 }}
                  />
                  <img
                    src={img.preview}
                    alt={img.name}
                    className={`prod-image-preview ${thumbnailIndex === index ? 'border border-primary border-3' : 'border'}`}
                    style={{ 
                      width: "100px", 
                      height: "100px", 
                      objectFit: "cover",
                      borderRadius: "4px"
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showProgress && (
        <UploadProgressBar
          uploadProgressValue={uploadProgress}
          progressBarId="progressAddProduct"
          style={{ display: "block" }}
        />
      )}

      <button type="submit" className="btn btn-primary">
        Add Product
      </button>
    </form>
  );
};

export default AddProduct;